import { formatPrice } from "@/functions/billing";
import { CreditAmount } from '@/functions/credit-functions';
import { getJobDomains } from "@/functions/job-posting";
import { getPromotions, promotionHasLogo, PromotionType, usePromotionPrices, getDefaultPackagePrices, formatPriceDisplay } from "@/states/promotion-state";
import { computed, defineComponent, onMounted, PropType, reactive, watch } from "@vue/composition-api";

export default defineComponent({
    props: {
        ad: {
            type: Object as PropType<{ id?: number; promotion: PromotionType}>,
            required: false,
        },
        selected: {
            type: String as PropType<PromotionType>,
            default: 'none',
        },
        credits: {
            type: Array as PropType<CreditAmount[]>,
            default: () => [],
        },
        creditEnabled: {
            type: Boolean,
            default: false,
        },
        companyId: {
            type: Number,
            required: true,
        },
    },
    emits: {
        'update:selected': (type: PromotionType) => true,
    },
    setup(props, context) {
        const promotions = getPromotions();

        const promotionPrices = usePromotionPrices();

        const state = reactive({
            domains: [] as string[],
            showDomains: false,
        });

        const visibleDomains = computed(() => state.domains.slice(0, 2));

        const adPromotion = computed(() => props.ad ? props.ad.promotion : 'none');
        const disabled = computed(() => adPromotion.value !== 'none');

        const isCreditEnabled = (type: PromotionType) => {
            return props.creditEnabled && props.credits.some(credit => credit.type === type && credit.amount > 0);
        };

        const getCreditAmountLeft = (type: PromotionType) => {
            return !props.creditEnabled ? 0 : props.credits.find(credit => credit.type === type)?.amount ?? 0;
        };

        const togglePackage = (type: PromotionType) => {
            if(disabled.value) {
                return;
            }

            context.emit('update:selected', type);
        };

        const getPromotionPrice = (type: PromotionType) => {
            if(type === 'none') {
                return 0;
            }

            const foundCompanyPrices = promotionPrices.prices.find(item => item.companyId === props.companyId);

            if (!foundCompanyPrices) {
              if (isCreditEnabled(type)) {
                return getDefaultPackagePrices(type).tokenPrice;
              }
      
              return getDefaultPackagePrices(type).price;
            }
      
            if (isCreditEnabled(type)) {
              return foundCompanyPrices.prices['credit'][type] ?? getDefaultPackagePrices(type).tokenPrice;
            }
      
            return foundCompanyPrices.prices['monetary'][type] ?? getDefaultPackagePrices(type);
        };

        const updateScopes = async (adId: number) => {
            if(!props.ad) {
                state.domains = [];
                return;
            }
    
            state.domains = [...await getJobDomains(adId)].filter(domain => domain !== 'ledigajobb.se');
        };

        onMounted(async () => {
            ($('[data-toggle="tooltip"]') as any).tooltip({ placement: 'right' });

            if(props.ad?.id) {
                await updateScopes(props.ad.id);
            }
        });

        watch(() => props.ad, (current, prev) => {
          if(!current) {
            state.domains = [];
          }
          else if(prev?.id !== current.id && current.id) {
            updateScopes(current.id);
          }
        }, {immediate: true, deep: true});

        return {
            state,
            promotions,
            adPromotion,
            disabled,
            togglePackage,
            getPromotionPrice,
            formatPrice,
            visibleDomains,
            isCreditEnabled,
            getCreditAmountLeft,
            promotionHasLogo,
        }
    }
});
